import React, {useRef, useState} from 'react';
import {FileDropzoneContainer, FileList, DragDropArea, IconCircle} from "./FileDropzone.styled";
import {Box, Typography} from "@mui/material";
import {theme, tokens} from "../../../theme";
import {useTranslation} from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import {ReactComponent as UploadIcon} from '../../../assets/icons/upload_ocr.svg';

export type FileValidator = (incoming: File[], current: File[]) => string | null;

interface FileDropzoneProps {
    value: File[];
    accept: string[];
    onChange: (files: File[]) => void;
    title: string;
    formatHint: string;
    onReject?: (message: string) => void;
    multiple?: boolean;
    disabled?: boolean;
    validate?: FileValidator;
}

export const FileDropzone = (props: FileDropzoneProps) => {
    const { value, title, formatHint, onChange, onReject, validate, multiple = false, accept, disabled } = props;

    const {t} = useTranslation();

    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    const handleFiles = (list: FileList | null) => {
        if (!list) return;
        const files = Array.from(list);

        if (!multiple && files.length !== 1) {
            return onReject?.(t('fileDropzone.oneFileUploadError') as string);
        }

        const error = validate?.(files, value);
        if (error) return onReject?.(error);


        onChange(multiple ? [...value, ...files] : files);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleFiles(e.target.files);
        e.target.value = '';
    };

    const onDragOver = (e: React.DragEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();

        setIsDragging(false);

        if (!disabled) handleFiles(e.dataTransfer.files);
    };

    const handleRemove = (e: React.MouseEvent, index: number) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(value.filter((_, i) => i !== index));
    };

    return (
        <FileDropzoneContainer>
            <input
                ref={inputRef}
                type="file"
                multiple={multiple}
                accept={accept?.join(',')}
                hidden
                style={{display: 'none'}}
                onChange={handleInputChange}
                aria-hidden="true"
                tabIndex={-1}
            />

            <DragDropArea
                disableRipple
                disabled={disabled}
                onClick={() => inputRef.current?.click()}
                onDragOver={onDragOver}
                onDragLeave={() => setIsDragging(false)}
                onDrop={onDrop}
                $isDragging={isDragging}
                component="div"
                aria-label={title}
                tabIndex={disabled ? -1 : 0}
            >
                <IconCircle>
                    <UploadIcon aria-hidden="true" />
                </IconCircle>

                <Typography variant="body1" fontWeight={tokens.fontWeight.medium} sx={{ color: theme.palette.text.primary }}>
                    {title}
                </Typography>

                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    {formatHint}
                </Typography>

                {value.length > 0 && (
                    <Box flexDirection="column" display="flex" alignItems="center" gap={2} pl={3} pr={3}>
                        <Typography variant="body2" color="text.hint" mt={3}>
                            {t('fileDropzone.selectedFiles')}
                        </Typography>

                        <FileList>
                            {value.map((f, i) => (
                                <li key={`${f.name}-${f.lastModified}`}>
                                    <span>{f.name}</span>
                                    <button type="button"
                                            aria-label={t('fileDropzone.deleteFile', {fileName: f.name}) as string}
                                            onClick={(e) => handleRemove(e, i)}
                                    >
                                        <CloseIcon sx={{ fontSize: 16, ml: 1 }} />
                                    </button>
                                </li>
                            ))}
                        </FileList>
                    </Box>
                )}
            </DragDropArea>
        </FileDropzoneContainer>
    );
};

export default FileDropzone;